import { css } from "@emotion/css";
import { type EnvironmentResource, type LibraryVariableSetResource } from "@octopusdeploy/octopus-server-client";
import React, { useMemo, useState } from "react";
import { TemplatesFiltersBar } from "~/areas/projects/components/Variables/TenantVariables/FiltersBar";
import { MissingValuesCallout } from "~/areas/projects/components/Variables/TenantVariables/MissingValuesCallout";
import { useScopedCommonTemplatesFilterState } from "~/areas/projects/components/Variables/TenantVariables/ScopedTenantVariables/useScopedCommonTemplatesFilterState";
import type { LoadedSourceItems } from "~/areas/projects/components/Variables/TenantVariables/useTenantVariableResourceOptions";
import NumberedPagingBar from "../components/Paging/NumberedPagingBar";
import { type RowData, ScopedTenantTemplateDataGrid } from "./ScopedTenantTemplateDataGrid";
import type { ModelAction, TenantPageCommonVariableModel } from "./tenantVariablePageModel";
export interface ScopedLibrarySetTemplateListProps {
    spaceId: string;
    variables: TenantPageCommonVariableModel[];
    libraryVariableSets: LibraryVariableSetResource[];
    environments: EnvironmentResource[];
    variableSources: LoadedSourceItems;
    doBusyTask: (action: () => Promise<void>) => Promise<boolean>;
    onVariableChange: (action: ModelAction) => void;
}
export function ScopedLibrarySetTemplateList(props: ScopedLibrarySetTemplateListProps): JSX.Element {
    const { spaceId, variables, onVariableChange, ...gridProps } = props;
    const { libraryVariableSets, environments } = gridProps;
    const templates = libraryVariableSets.flatMap((l) => l.Templates);
    const [filterState, setFilterState] = useScopedCommonTemplatesFilterState();
    const [pageSize, setPageSize] = useState<number>(30);
    const templateIdToIndex = templates.reduce((map: Record<string, number>, template, index) => {
        map[template.Id] = index;
        return map;
    }, {});
    const data = useMemo(() => variables
        .reduce((acc: RowData[], v) => {
        const template = templates.find((t) => t.Id === v.TemplateId);
        const libraryVariableSet = libraryVariableSets.find((l) => l.Id === v.LibraryVariableSetId);
        if (!template || !libraryVariableSet)
            return acc; // This should never happen
        return [
            ...acc,
            {
                Id: v.Id ?? "",
                Template: template,
                Container: { Id: libraryVariableSet.Id, Name: libraryVariableSet.Name },
                Value: v.Value,
                Status: v.Status,
                EnvironmentIds: v.Scope.EnvironmentIds,
                IsTemplateChild: false, // Will be recalculated after filtering
                IsTenantChild: false,
                HasValueChanged: v.HasValueChanged,
                HasScopeChanged: v.HasScopeChanged,
            },
        ];
    }, [])
        .sort((a, b) => {
        const indexA = templateIdToIndex[a.Template.Id];
        const indexB = templateIdToIndex[b.Template.Id];
        if (indexA < indexB)
            return -1;
        if (indexA > indexB)
            return 1;
        return 0;
    }), [variables, templates, libraryVariableSets, templateIdToIndex]);
    const filteredData = useMemo(() => {
        const filtered = data
            .filter((r) => {
            if (!filterState.filterByLibraryVariableSetId)
                return true;
            return r.Container.Id === filterState.filterByLibraryVariableSetId;
        })
            .filter((r) => {
            if (!filterState.filterByExcludedLibraryVariableSetId)
                return true;
            return r.Container.Id !== filterState.filterByExcludedLibraryVariableSetId;
        })
            .filter((r) => {
            if (!filterState.filterByName)
                return true;
            return r.Template.Name.toLowerCase().includes(filterState.filterByName.toLowerCase());
        })
            .filter((r) => {
            switch (filterState.filterByValueStatus) {
                case "missing":
                    return r.Status === "missing" || r.Status === "new";
                case "overridden":
                    return r.Status === "overridden" || r.Status === "new";
                default:
                    return true;
            }
        })
            .filter((r) => {
            if (!filterState.filterByEnvironment)
                return true;
            return r.EnvironmentIds.length === 0 || r.EnvironmentIds.includes(filterState.filterByEnvironment);
        })
            .filter((r) => {
            if (!filterState.filterByExcludedEnvironment)
                return true;
            return !r.EnvironmentIds.includes(filterState.filterByExcludedEnvironment);
        });
        // Recalculate IsTemplateChild after filtering to ensure the first visible row shows the template name
        const visibleTemplates = new Set<string>();
        return filtered.map((row) => {
            const key = `${row.Template.Id}-${row.Container.Id}`;
            const isChild = visibleTemplates.has(key);
            visibleTemplates.add(key);
            return { ...row, IsTemplateChild: isChild };
        });
    }, [data, filterState]);
    const paginatedData = useMemo(() => filteredData.slice((filterState.pageNumber - 1) * pageSize, filterState.pageNumber * pageSize), [filteredData, filterState.pageNumber, pageSize]);
    const missingSetIds = new Set(variables.filter((v) => v.Status === "missing").map((v) => v.LibraryVariableSetId));
    return (<>
            {missingSetIds.size !== 0 && <MissingValuesCallout filterState={filterState} setFilterState={setFilterState}/>}
            <TemplatesFiltersBar totalVariablesCount={data.length} filteredVariablesCount={filteredData.length} filterState={filterState} onFilterChange={setFilterState} filterData={{ tenants: null, environments: environments, tagSets: null }} libraryVariableSets={libraryVariableSets} unsavedChangesCount={0}/>
            <ScopedTenantTemplateDataGrid {...gridProps} data={paginatedData} enabledColumns={[
            { type: "Template", width: "30%" },
            { type: "Value", width: "30%" },
            { type: "Environments", width: "30%" },
            { type: "Actions", width: "10%" },
        ]} hasEditPermissions={true} onVariableValueUpdate={(variableId, value) => onVariableChange({ action: "updateValue", variableId, value: value ?? null })} onVariableScopeUpdate={(variableId, environmentIds) => onVariableChange({ action: "updateScope", variableId, environmentIds: environmentIds })} onVariableRemove={(variableId) => onVariableChange({ variableId, action: "remove" })} onVariableAdd={(_, libraryVariableSetId, templateId) => onVariableChange({ action: "add", libraryVariableSetId, templateId })} groupNames={libraryVariableSets.map((v) => v.Name)}/>

            <div className={listStyles.pagingBarStyles}>
                <NumberedPagingBar totalItems={filteredData.length} pageNumber={filterState.pageNumber} pageSize={pageSize} pageSizeOptions={[30, 50, 100]} onPagingSelectionChange={setPageState}/>
            </div>
        </>);
    function setPageState(pageNumber: number, newPageSize: number): void {
        setFilterState({ ...filterState, pageNumber: pageNumber });
        setPageSize(newPageSize);
    }
}
const listStyles = {
    pagingBarStyles: css({
        padding: "0 1rem",
    }),
};
